import { FileUpload, InsertDriveFileOutlined } from '@mui/icons-material';
import { Box, Link, Theme, Typography } from '@mui/material';
import { parse, unparse } from 'papaparse';
import { useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';

import { Button, ErrorAlert } from '@/components';
import { log } from '@/services';
import { PlanLimits } from '@/store';
import { transformCsvData } from '@/utils';

import { CSV_CONFIG, CSV_ERRORS, SAMPLE_DOWNLOAD_DATA } from '../constants';
import { isOnlyUniqueEmails, isUnderSeatCap } from '../utils';

const SAMPLE_DOWNLOAD_CSV = unparse(SAMPLE_DOWNLOAD_DATA);
const SAMPLE_CSV_URL = URL.createObjectURL(
  new Blob([SAMPLE_DOWNLOAD_CSV], { type: 'text/csv;charset=utf-8;' }),
);

interface CsvUploadProps {
  label: string;
  onPreview: (data: { name: string; email: string }[]) => void;
  planLimits: PlanLimits;
  activeMemberCount: number;
}

interface CsvUploadState {
  error: string;
  fileName: string;
  data: { name: string; email: string }[];
}

export const CsvUpload = ({
  label,
  onPreview,
  planLimits,
  activeMemberCount,
}: CsvUploadProps) => {
  const [state, setState] = useState<CsvUploadState>({
    error: '',
    fileName: '',
    data: [],
  });

  const onDrop = (acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (!file) return;
    setState({
      error: '',
      fileName: file.name,
      data: [],
    });

    parse(file, {
      worker: true,
      header: true,
      skipEmptyLines: 'greedy',
      error: err => {
        log('CSV Parse Error:', err);
        setState({
          error: CSV_ERRORS.generic,
          fileName: '',
          data: [],
        });
      },
      complete: results => {
        const rowCount = results.data.length;
        if (rowCount === 0) {
          setState({
            error: CSV_ERRORS.noData,
            fileName: '',
            data: [],
          });
          return;
        }

        if (!isUnderSeatCap(planLimits, rowCount, activeMemberCount)) {
          const availableSeats =
            planLimits?.seatCap &&
            Math.max(planLimits.seatCap.seatCap - activeMemberCount, 0);

          const error = `Member limit reached. This CSV contains ${rowCount} new members, however you only have space for ${
            availableSeats
          } additional members.`;

          setState({
            error,
            fileName: '',
            data: [],
          });
          return;
        }

        const transformedData = transformCsvData(results.data);
        if (!transformedData) {
          setState({
            error: CSV_ERRORS.invalidColumns,
            fileName: '',
            data: [],
          });
          return;
        }

        if (!isOnlyUniqueEmails(transformedData)) {
          setState({
            error: CSV_ERRORS.duplicateEmails,
            fileName: '',
            data: [],
          });
          return;
        }

        setState({
          error: '',
          fileName: file.name,
          data: transformedData,
        });
      },
    });
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: CSV_CONFIG.acceptedFileTypes,
    onDrop,
    onError: error => {
      log('CSV Dropzone Error:', error);
      setState({
        error: CSV_ERRORS.generic,
        fileName: '',
        data: [],
      });
    },
    onDropRejected: fileRejections => {
      const rejection = fileRejections[0];
      const error = rejection.errors[0];
      let errorMessage = '';
      switch (error.code) {
        case 'file-invalid-type':
          errorMessage = CSV_ERRORS.invalidFileType;
          break;
        case 'file-too-large':
          errorMessage = CSV_ERRORS.size;
          break;
        case 'too-many-files':
          errorMessage = CSV_ERRORS.tooManyFiles;
          break;
        default:
          errorMessage = CSV_ERRORS.generic;
      }
      setState({
        error: errorMessage,
        fileName: '',
        data: [],
      });
    },
    multiple: false,
    maxSize: CSV_CONFIG.maxFileSize,
  });

  const dropzoneRootProps = getRootProps({
    role: 'button',
    tabIndex: 0,
    'aria-label': 'Drag and drop',
  });
  const dropzoneInputProps = getInputProps();

  useEffect(() => {
    return () => {
      URL.revokeObjectURL(SAMPLE_CSV_URL);
    };
  }, []);

  return (
    <Box>
      <Typography variant="body3" sx={{ mb: 0.5 }} component="label">
        {label}
      </Typography>
      <Box
        {...dropzoneRootProps}
        sx={[
          styles.dropzone,
          isDragActive || state.fileName ? styles.gradient : {},
        ]}>
        <input {...dropzoneInputProps} />
        {isDragActive ? (
          <>
            <FileUpload sx={{ color: 'secondary.main', fontSize: 32 }} />
            <Typography variant="body2">Drop CSV file</Typography>
          </>
        ) : state.fileName ? (
          <>
            <InsertDriveFileOutlined
              sx={{ color: 'secondary.main', fontSize: 32 }}
            />
            <Typography variant="body2">{state.fileName}</Typography>
          </>
        ) : (
          <>
            <InsertDriveFileOutlined sx={{ color: 'grey100', fontSize: 32 }} />
            <Typography variant="body2" sx={{ textAlign: 'center' }}>
              Drag CSV file here <br />
              or{' '}
              <Box component="span" sx={{ color: 'secondary.main' }}>
                click to upload
              </Box>
            </Typography>
          </>
        )}
      </Box>
      {state.error && (
        <Box sx={{ mt: 2 }}>
          <ErrorAlert error={state.error} />
        </Box>
      )}
      <Box sx={styles.footer}>
        <Link
          underline="hover"
          href={SAMPLE_CSV_URL}
          color="secondary.main"
          download="members-example-download.csv">
          Download sample CSV
        </Link>
        <Button
          onClick={() => onPreview?.(state.data)}
          variant="primary"
          fullWidth={false}
          skinny={false}
          label="Preview"
          disabled={!state.fileName}
        />
      </Box>
    </Box>
  );
};

const styles = {
  dropzone: {
    borderWidth: 1,
    borderStyle: 'dashed',
    borderColor: 'grey100',
    height: '200px',
    borderRadius: 2,
    display: 'flex',
    flexDirection: 'column',
    gap: 1,
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
  },
  gradient: (theme: Theme) => ({
    background: theme.palette.gradients.secondary,
    borderStyle: 'solid',
    borderColor: theme.palette.secondary.active,
  }),
  icon: {
    color: 'grey100',
  },
  footer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    mt: 2,
  },
};
